import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Menu, 
  X, 
  ChevronDown, 
  User, 
  LogOut, 
  Settings, 
  TrendingUp, 
  Wallet, 
  BarChart2, 
  Repeat, 
  Bot, 
  Calendar, 
  Award, 
  Home,
  Landmark,
  TrendingDown,
  Briefcase,
  ChevronUp,
  Search,
  DollarSign,
  Bitcoin,
  CreditCard,
  BarChart,
  Gem
} from 'lucide-react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { TradingMode, UserStatus } from '../App';

interface MarketData {
  symbol: string;
  price: number;
  volume_24h: number;
  change_24h: number;
  market_cap?: number;
  high_price_24h?: number;
  low_price_24h?: number;
  funding_rate?: number;
  open_interest?: number;
}

interface HeaderProps {
  tradingMode: TradingMode;
  setTradingMode: (mode: TradingMode) => void;
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  currentPrice: number;
  marketData?: MarketData;
  usdtBalance: number;
  btcBalance: number;
  totalPortfolioValue: number;
  user: SupabaseUser;
  signOut: () => void;
  marketDataList: MarketData[];
  ticker?: {
    symbol: string;
    price: number;
    priceChange: number;
    priceChangePercent: number;
  } | null;
  userStatus: UserStatus;
}

// Map user status to icon and color
const userStatusConfig: Record<UserStatus, { icon: React.ReactNode, color: string, label: string }> = {
  'No-Coiner': { 
    icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>, 
    color: 'text-gray-400', 
    label: 'No-Coiner' 
  },
  'Shrimp': { 
    icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z"></path></svg>, 
    color: 'text-pink-400', 
    label: 'Shrimp' 
  },
  'Crab': { 
    icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10H2A10 10 0 0 0 12 2Z"></path></svg>, 
    color: 'text-red-400', 
    label: 'Crab' 
  },
  'Octopus': { 
    icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 9V3H15"></path><path d="M3 15v6h6"></path><path d="M21 3l-9 9"></path><path d="M3 21l9-9"></path></svg>, 
    color: 'text-purple-400', 
    label: 'Octopus' 
  },
  'Dolphin': { 
    icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v19"></path><path d="M5 8h14"></path><path d="M5 16h14"></path></svg>, 
    color: 'text-blue-400', 
    label: 'Dolphin' 
  },
  'Shark': { 
    icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"></path></svg>, 
    color: 'text-green-400', 
    label: 'Shark' 
  },
  'Whale': { 
    icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20"></path><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>, 
    color: 'text-indigo-400', 
    label: 'Whale' 
  },
  'Humpback': { 
    icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m9 12 2 2 4-4"></path></svg>, 
    color: 'text-purple-600', 
    label: 'Humpback' 
  }
};

// Instrument type icons
const instrumentTypeIcons: Record<string, React.ReactNode> = {
  'crypto': <Bitcoin size={16} className="text-orange-400" />,
  'forex': <DollarSign size={16} className="text-green-400" />,
  'commodity': <Gem size={16} className="text-amber-400" />,
  'stock': <BarChart size={16} className="text-blue-400" />,
  'index': <BarChart2 size={16} className="text-purple-400" />,
  'other': <Briefcase size={16} className="text-gray-400" />
};

// Mapping of crypto symbols to full names
const cryptoNames: Record<string, string> = {
  'BTC': 'Bitcoin',
  'ETH': 'Ethereum',
  'BNB': 'Binance Coin',
  'SOL': 'Solana',
  'XRP': 'Ripple',
  'ADA': 'Cardano',
  'DOGE': 'Dogecoin',
  'MATIC': 'Polygon',
  'DOT': 'Polkadot',
  'AVAX': 'Avalanche',
  'LINK': 'Chainlink',
  'UNI': 'Uniswap',
  'SHIB': 'Shiba Inu',
  'LTC': 'Litecoin',
  'ATOM': 'Cosmos',
  'XLM': 'Stellar',
  'NEAR': 'Near Protocol',
  'ALGO': 'Algorand',
  'FTM': 'Fantom',
  'HBAR': 'Hedera',
  'VET': 'VeChain',
  'MANA': 'Decentraland',
  'SAND': 'The Sandbox',
  'AXS': 'Axie Infinity',
  'APE': 'ApeCoin',
  'FIL': 'Filecoin',
  'EOS': 'EOS',
  'EGLD': 'Elrond',
  'XTZ': 'Tezos',
  'AAVE': 'Aave',
  'MKR': 'Maker',
  'COMP': 'Compound',
  'SUSHI': 'SushiSwap',
  'CRV': 'Curve DAO',
  'BAT': 'Basic Attention Token',
  'ZEC': 'Zcash',
  'DASH': 'Dash',
  'XMR': 'Monero',
  'ENJ': 'Enjin Coin',
  'CHZ': 'Chiliz',
  'ONE': 'Harmony',
  'HOT': 'Holo',
  'ZIL': 'Zilliqa',
  'IOTA': 'IOTA',
  'XEM': 'NEM',
  'QTUM': 'Qtum',
  'ICX': 'ICON',
  'OMG': 'OMG Network',
  'ZRX': 'Ox',
  'KSM': 'Kusama',
  'WAVES': 'Waves',
  'NEO': 'NEO',
  'KAVA': 'Kava',
  'ONT': 'Ontology',
  'CELO': 'Celo',
  'THETA': 'Theta Network',
  'LUNA': 'Terra',
  'CAKE': 'PancakeSwap',
  'FARM': 'Harvest Finance',
  'TRX': 'TRON',
  'DCR': 'Decred',
  'HYPE': 'Hypersign',
  'BCH': 'Bitcoin Cash',
  'SUI': 'Sui',
  'LINK': 'Chainlink',
  'AVAX': 'Avalanche',
  'XMR': 'Monero',
  'TON': 'Toncoin',
  'LTC': 'Litecoin',
  'HBAR': 'Hedera'
};

// Forex pair names
const forexNames: Record<string, string> = {
  'EURUSD': 'Euro / US Dollar',
  'GBPUSD': 'British Pound / US Dollar',
  'USDJPY': 'US Dollar / Japanese Yen',
  'AUDUSD': 'Australian Dollar / US Dollar',
  'USDCAD': 'US Dollar / Canadian Dollar',
  'USDCHF': 'US Dollar / Swiss Franc',
  'NZDUSD': 'New Zealand Dollar / US Dollar',
  'EURJPY': 'Euro / Japanese Yen',
  'GBPJPY': 'British Pound / Japanese Yen',
  'EURGBP': 'Euro / British Pound'
};

// Commodity names
const commodityNames: Record<string, string> = {
  'XAUUSD': 'Gold',
  'XAGUSD': 'Silver',
  'XPTUSD': 'Platinum',
  'XPDUSD': 'Palladium',
  'WTIUSD': 'Crude Oil WTI',
  'BRTUSD': 'Brent Crude Oil',
  'NGUSD': 'Natural Gas'
};

const Header: React.FC<HeaderProps> = ({ 
  tradingMode, 
  setTradingMode, 
  selectedPair, 
  setSelectedPair, 
  currentPrice, 
  marketData, 
  usdtBalance, 
  btcBalance, 
  totalPortfolioValue, 
  user, 
  signOut,
  marketDataList,
  ticker,
  userStatus
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isPairDropdownOpen, setIsPairDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const userMenuRef = useRef<HTMLDivElement>(null);
  const pairDropdownRef = useRef<HTMLDivElement>(null);
  
  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (pairDropdownRef.current && !pairDropdownRef.current.contains(event.target as Node)) {
        setIsPairDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Format price with appropriate precision
  const formatPrice = (price: number) => {
    if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 1) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 });
  };
  
  // Get price change color
  const getPriceChangeColor = (change: number) => {
    return change >= 0 ? 'text-green-500' : 'text-red-500';
  };
  
  // Get price change icon
  const getPriceChangeIcon = (change: number) => {
    return change >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />;
  };

  // Get status config for current user
  const statusConfig = userStatusConfig[userStatus];

  // Format pair for display
  const formatPairForDisplay = (pair: string) => {
    // For forex pairs like EURUSD
    if (pair.length === 6 && !pair.includes('USDT')) {
      return `${pair.substring(0, 3)}/${pair.substring(3, 6)}`;
    }
    // For crypto pairs like BTCUSDT
    if (pair.endsWith('USDT')) {
      return `${pair.replace('USDT', '')}/${pair.substring(pair.length - 4)}`;
    }
    // For commodity pairs like XAUUSD
    if (pair.startsWith('XAU') || pair.startsWith('XAG') || pair.startsWith('XPT')) {
      return `${pair.substring(0, 3)}/${pair.substring(3)}`;
    }
    return pair;
  };

  // Get full name for a trading pair
  const getPairFullName = (symbol: string): string => {
    // For crypto pairs
    if (symbol.endsWith('USDT')) {
      const base = symbol.replace('USDT', '');
      return cryptoNames[base] || base;
    }
    
    // For forex pairs
    if (forexNames[symbol]) {
      return forexNames[symbol];
    }
    
    // For commodity pairs
    if (commodityNames[symbol]) {
      return commodityNames[symbol];
    }
    
    return symbol;
  };

  // Determine instrument type based on symbol
  const getInstrumentType = (symbol: string): string => {
    // Crypto pairs
    if (symbol.endsWith('USDT') || symbol.endsWith('BTC') || symbol.endsWith('ETH')) {
      return 'crypto';
    }
    
    // Forex pairs
    const forexCurrencies = ['USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'NZD'];
    if (symbol.length === 6 && forexCurrencies.some(curr => symbol.includes(curr))) {
      return 'forex';
    }
    
    // Commodities
    if (symbol.startsWith('XAU') || symbol.startsWith('XAG') || symbol.startsWith('XPT') || 
        symbol.startsWith('XPD') || symbol === 'WTIUSD' || symbol === 'BRTUSD' || 
        symbol === 'NGUSD' || symbol.includes('COPPER')) {
      return 'commodity';
    }
    
    // Stocks
    const stockSymbols = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NFLX', 'NVDA'];
    if (stockSymbols.includes(symbol)) {
      return 'stock';
    }
    
    // Indices
    if (symbol.includes('SPX') || symbol.includes('NDX') || symbol.includes('DJI')) {
      return 'index';
    }
    
    return 'other';
  };

  // Filter and sort market data for dropdown
  const getFilteredAndSortedMarketData = useMemo(() => {
    // First, filter based on trading mode
    let filteredData = [...marketDataList];
    
    if (tradingMode === 'futures') {
      filteredData = filteredData.filter(item => item.symbol.endsWith('USDT'));
    } else if (tradingMode === 'cfd') {
      filteredData = filteredData.filter(item => {
        const type = getInstrumentType(item.symbol);
        return type === 'forex' || type === 'commodity' || type === 'stock' || type === 'index';
      });
    }
    
    // Then apply search filter if there's a search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredData = filteredData.filter(item => {
        // Check if symbol matches
        if (item.symbol.toLowerCase().includes(term)) {
          return true;
        }
        
        // Check if formatted pair matches
        if (formatPairForDisplay(item.symbol).toLowerCase().includes(term)) {
          return true;
        }
        
        // Check if full name matches
        const fullName = getPairFullName(item.symbol).toLowerCase();
        if (fullName.includes(term)) {
          return true;
        }
        
        // For crypto pairs, check if the base currency name matches
        if (item.symbol.endsWith('USDT')) {
          const base = item.symbol.replace('USDT', '');
          const baseName = cryptoNames[base]?.toLowerCase() || '';
          if (baseName.includes(term)) {
            return true;
          }
        }
        
        return false;
      });
    }
    
    // Add instrument type to each item
    const dataWithType = filteredData.map(item => ({
      ...item,
      instrumentType: getInstrumentType(item.symbol)
    }));
    
    // Sort by market cap (descending) or alphabetically if market cap is not available
    dataWithType.sort((a, b) => {
      // First by market cap (if available)
      if (a.market_cap && b.market_cap) {
        return b.market_cap - a.market_cap;
      } else if (a.market_cap) {
        return -1; // a has market cap, b doesn't
      } else if (b.market_cap) {
        return 1; // b has market cap, a doesn't
      }
      
      // Then by volume (if market cap not available)
      if (a.volume_24h && b.volume_24h) {
        return b.volume_24h - a.volume_24h;
      }
      
      // Finally alphabetically
      return a.symbol.localeCompare(b.symbol);
    });
    
    // Group by instrument type
    const groupedData: Record<string, typeof dataWithType> = {};
    
    dataWithType.forEach(item => {
      if (!groupedData[item.instrumentType]) {
        groupedData[item.instrumentType] = [];
      }
      groupedData[item.instrumentType].push(item);
    });
    
    return groupedData;
  }, [marketDataList, tradingMode, searchTerm]);

  // Get instrument type display name
  const getInstrumentTypeDisplay = (type: string): string => {
    const typeMap: Record<string, string> = {
      'crypto': 'Cryptocurrencies',
      'forex': 'Forex Pairs',
      'commodity': 'Commodities',
      'stock': 'Stocks',
      'index': 'Indices',
      'other': 'Other Markets'
    };
    
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <header className="bg-slate-800/80 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center" onClick={() => setTradingMode('home')}>
              <div className="flex items-center">
                <div className="relative">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center transform rotate-45">
                    <TrendingUp size={16} className="text-white transform -rotate-45" />
                  </div>
                </div>
                <span className="ml-2 text-xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">WhiteRock24</span>
              </div>
            </Link>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-slate-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white p-2"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6">
            <button 
              onClick={() => setTradingMode('home')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                tradingMode === 'home' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Home size={16} />
                <span>Home</span>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('swap')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                tradingMode === 'swap' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Repeat size={16} />
                <span>Swap</span>
              </div>
            </button>
            
            <button 
              onClick={() => {
                setTradingMode('futures');
                setSelectedPair('BTCUSDT');
              }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                tradingMode === 'futures' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <TrendingUp size={16} />
                <span>Futures</span>
              </div>
            </button>
            
            <button 
              onClick={() => {
                setTradingMode('cfd');
                setSelectedPair('EURUSD');
              }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                tradingMode === 'cfd' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <BarChart2 size={16} />
                <span>CFD</span>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('prop_firm')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                tradingMode === 'prop_firm' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Award size={16} />
                <span>Prop Firm</span>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('robot')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                tradingMode === 'robot' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Bot size={16} />
                <span>Robot</span>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('events')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                tradingMode === 'events' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Calendar size={16} />
                <span>Events</span>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('staking')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                tradingMode === 'staking' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Landmark size={16} />
                <span>Staking</span>
              </div>
            </button>
          </nav>
          
          {/* Right Section - User Info, Balance, etc. */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Trading Pair Selector */}
            {(tradingMode === 'futures' || tradingMode === 'cfd') && (
              <div className="relative" ref={pairDropdownRef}>
                <button
                  onClick={() => setIsPairDropdownOpen(!isPairDropdownOpen)}
                  className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-white hover:bg-slate-700"
                >
                  <span className="font-medium">{formatPairForDisplay(selectedPair)}</span>
                  <span className="text-xs text-slate-400">Perp</span>
                  {isPairDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                
                {isPairDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-80 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50">
                    <div className="p-2 sticky top-0 bg-slate-900 border-b border-slate-700 z-10">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search pairs..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 pl-9 bg-slate-800 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
                      </div>
                    </div>
                    
                    <div className="max-h-72 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                      {Object.entries(getFilteredAndSortedMarketData).map(([type, items]) => (
                        <div key={type}>
                          <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-300 uppercase tracking-wider flex items-center">
                            {instrumentTypeIcons[type] || <Briefcase size={14} className="mr-1.5" />}
                            <span>{getInstrumentTypeDisplay(type)}</span>
                          </div>
                          
                          {items.map((item) => (
                            <button
                              key={item.symbol}
                              onClick={() => {
                                setSelectedPair(item.symbol);
                                setIsPairDropdownOpen(false);
                              }}
                              className={`flex items-center justify-between w-full px-3 py-2.5 text-sm hover:bg-slate-700/50 ${
                                selectedPair === item.symbol ? 'bg-blue-900/20' : 'text-white'
                              }`}
                            >
                              <div className="flex items-center">
                                <Bitcoin size={16} className="mr-2 text-blue-400" />
                                <div className="flex flex-col items-start">
                                  <span className="font-medium">{formatPairForDisplay(item.symbol)}</span>
                                  <span className="text-xs text-slate-400">{getPairFullName(item.symbol)}</span>
                                </div>
                              </div>
                              <div className="flex items-center">
                                <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-800 text-slate-300 mr-2">
                                  CRYPTO
                                </span>
                                <span className={`text-xs font-medium ${getPriceChangeColor(item.change_24h)}`}>
                                  {item.change_24h >= 0 ? '+' : ''}{item.change_24h.toFixed(2)}%
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      ))}
                      
                      {Object.keys(getFilteredAndSortedMarketData).length === 0 && (
                        <div className="px-4 py-3 text-sm text-slate-400 text-center">
                          No matching pairs found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Current Price - FIXED WIDTH ADDED HERE */}
            <div className="hidden lg:block min-w-[150px] text-right">
              <div className="text-xs text-slate-400">Price</div>
              <div className="flex items-center justify-end">
                <span className="text-white font-semibold">${formatPrice(currentPrice)}</span>
                {marketData && (
                  <span className={`ml-2 text-xs flex items-center ${getPriceChangeColor(marketData.change_24h)}`}>
                    {getPriceChangeIcon(marketData.change_24h)}
                    <span className="ml-1">{marketData.change_24h.toFixed(2)}%</span>
                  </span>
                )}
              </div>
            </div>
            
            {/* User Status */}
            <div className="hidden lg:flex items-center px-3 py-1.5 rounded-lg bg-slate-700/50 border border-slate-600/50">
              <div className={`flex items-center ${statusConfig.color}`}>
                {statusConfig.icon}
                <span className="ml-1.5 text-sm font-medium">{statusConfig.label}</span>
              </div>
            </div>
            
            {/* Portfolio Value - FIXED WIDTH ADDED HERE */}
            <div className="hidden lg:block min-w-[180px] text-right">
              <div className="text-xs text-slate-400">Portfolio Value</div>
              <div className="text-white font-semibold">${totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            
            {/* Wallet Button */}
            <button 
              onClick={() => setTradingMode('wallet')}
              className={`hidden lg:flex items-center space-x-1 px-3 py-1.5 rounded-lg ${
                tradingMode === 'wallet' 
                  ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' 
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50 border border-slate-700'
              }`}
            >
              <Wallet size={16} />
              <span>Wallet</span>
            </button>
            
            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 text-slate-300 hover:text-white focus:outline-none"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">
                  {user.email?.charAt(0).toUpperCase()}
                </div>
                <ChevronDown size={16} className="text-slate-400" />
              </button>
              
              {/* User Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50">
                  <div className="p-3 border-b border-slate-700">
                    <p className="text-sm font-medium text-white truncate">{user.email}</p>
                    <div className="flex items-center mt-1">
                      <div className={`flex items-center ${statusConfig.color} text-xs`}>
                        {statusConfig.icon}
                        <span className="ml-1">{statusConfig.label}</span>
                      </div>
                    </div>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setTradingMode('profile');
                        setIsUserMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                    >
                      <User size={16} className="mr-2" />
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        setTradingMode('wallet');
                        setIsUserMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                    >
                      <Wallet size={16} className="mr-2" />
                      Wallet
                    </button>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                    >
                      <Settings size={16} className="mr-2" />
                      Settings
                    </button>
                    <button
                      onClick={() => {
                        signOut();
                        setIsUserMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    >
                      <LogOut size={16} className="mr-2" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-slate-800 border-t border-slate-700">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <button
              onClick={() => {
                setTradingMode('home');
                setIsMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                tradingMode === 'home' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Home size={18} className="mr-2" />
                Home
              </div>
            </button>
            
            <button
              onClick={() => {
                setTradingMode('swap');
                setIsMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                tradingMode === 'swap' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Repeat size={18} className="mr-2" />
                Swap
              </div>
            </button>
            
            <button
              onClick={() => {
                setTradingMode('futures');
                setSelectedPair('BTCUSDT');
                setIsMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                tradingMode === 'futures' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <TrendingUp size={18} className="mr-2" />
                Futures
              </div>
            </button>
            
            <button
              onClick={() => {
                setTradingMode('cfd');
                setSelectedPair('EURUSD');
                setIsMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                tradingMode === 'cfd' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <BarChart2 size={18} className="mr-2" />
                CFD
              </div>
            </button>
            
            <button
              onClick={() => {
                setTradingMode('prop_firm');
                setIsMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                tradingMode === 'prop_firm' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Award size={18} className="mr-2" />
                Prop Firm
              </div>
            </button>
            
            <button
              onClick={() => {
                setTradingMode('robot');
                setIsMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                tradingMode === 'robot' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Bot size={18} className="mr-2" />
                Robot
              </div>
            </button>
            
            <button
              onClick={() => {
                setTradingMode('events');
                setIsMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                tradingMode === 'events' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Calendar size={18} className="mr-2" />
                Events
              </div>
            </button>
            
            <button
              onClick={() => {
                setTradingMode('staking');
                setIsMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                tradingMode === 'staking' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Landmark size={18} className="mr-2" />
                Staking
              </div>
            </button>
            
            <button
              onClick={() => {
                setTradingMode('wallet');
                setIsMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                tradingMode === 'wallet' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <Wallet size={18} className="mr-2" />
                Wallet
              </div>
            </button>
            
            <button
              onClick={() => {
                setTradingMode('profile');
                setIsMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                tradingMode === 'profile' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <User size={18} className="mr-2" />
                Profile
              </div>
            </button>
            
            <button
              onClick={() => {
                signOut();
                setIsMenuOpen(false);
              }}
              className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300"
            >
              <div className="flex items-center">
                <LogOut size={18} className="mr-2" />
                Sign Out
              </div>
            </button>
          </div>
          
          {/* Mobile Trading Pair Selector */}
          {(tradingMode === 'futures' || tradingMode === 'cfd') && (
            <div className="px-4 py-2 border-t border-slate-700">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-400">Trading Pair</span>
                <div className="relative">
                  <select
                    value={selectedPair}
                    onChange={(e) => setSelectedPair(e.target.value)}
                    className="bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-1.5 text-sm appearance-none pr-8"
                  >
                    {Object.entries(getFilteredAndSortedMarketData).map(([type, items]) => (
                      <optgroup key={type} label={getInstrumentTypeDisplay(type)}>
                        {items.map((item) => (
                          <option key={item.symbol} value={item.symbol}>
                            {formatPairForDisplay(item.symbol)}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400" />
                </div>
              </div>
            </div>
          )}
          
          {/* Mobile User Status and Balance */}
          <div className="px-5 py-4 border-t border-slate-700">
            <div className="flex items-center justify-between mb-2">
              <div className={`flex items-center ${statusConfig.color}`}>
                {statusConfig.icon}
                <span className="ml-1.5 text-sm font-medium">{statusConfig.label}</span>
              </div>
              <div className="text-white font-semibold">${totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div className="flex justify-between text-xs text-slate-400">
              <span>{btcBalance.toFixed(8)} BTC</span>
              <span>{usdtBalance.toFixed(2)} USDT</span>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;